import type { ColoringEngine } from '../core/engine';
import type { ArtworkStore } from './ArtworkStore';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * AutoSave wires engine changes to the store. It saves:
 *   - after every completed stroke / eraser operation
 *   - after undo and reset
 *   - when the document becomes hidden (visibilitychange)
 *   - on pagehide (best-effort before suspension)
 *
 * Saves are coalesced with a short debounce so rapid changes don't thrash
 * IndexedDB, but visibility/pagehide force an immediate flush.
 */
export class AutoSave {
  private status: SaveStatus = 'idle';
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending = false;
  private disposed = false;
  private detachEngine: (() => void) | null = null;
  private statusListeners = new Set<(s: SaveStatus) => void>();

  constructor(
    private readonly engine: ColoringEngine,
    private readonly store: ArtworkStore,
    private readonly debounceMs = 250,
  ) {}

  start(): void {
    this.detachEngine = this.engine.onChange(() => this.schedule());
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibility);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.onPageHide);
    }
  }

  getStatus(): SaveStatus {
    return this.status;
  }
  onStatus(fn: (s: SaveStatus) => void): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }
  private setStatus(s: SaveStatus): void {
    this.status = s;
    for (const fn of this.statusListeners) fn(s);
  }

  private schedule(): void {
    if (this.disposed) return;
    this.pending = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), this.debounceMs);
  }

  /** Persist immediately (returns when the write settles). */
  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.pending) return;
    this.pending = false;
    this.setStatus('saving');
    try {
      await this.store.save(this.engine.toDocument());
      this.setStatus('saved');
    } catch {
      this.setStatus('error');
    }
  }

  private onVisibility = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      void this.flush();
    }
  };

  private onPageHide = (): void => {
    // Best-effort synchronous-ish flush before the page is frozen/unloaded.
    void this.flush();
  };

  dispose(): void {
    this.disposed = true;
    if (this.timer) clearTimeout(this.timer);
    this.detachEngine?.();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.onPageHide);
    }
  }
}
